import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiChevronLeft, FiRefreshCw } from 'react-icons/fi';
import { useAtom, useSetAtom } from 'jotai';
import {
  backgroundRefreshingAtom,
  lastDataRefreshAtom,
  TabType,
  activeTabAtom,
} from '../../atoms/v0/uiAtoms';
import {
  translationEnabledAtom,
  loadTranslationCacheAtom,
  triggerTranslationUpdateAtom
} from '../../atoms/v0/translationAtoms';
import { forceRefreshTasksAtom } from '../../atoms/v0/taskAtoms';
import { forceRefreshProjectsAtom } from '../../atoms/v0/projectAtoms';
import { useTranslation } from 'react-i18next';

interface TitleBarProps {
  title?: string;
  showBackButton?: boolean;
  showRefreshButton?: boolean;
  customAction?: React.ReactNode;
  onRefresh?: () => void;
}

export const TitleBar: React.FC<TitleBarProps> = ({
  title,
  showBackButton = false,
  showRefreshButton = true,
  customAction,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRefreshing] = useAtom(backgroundRefreshingAtom);
  const [lastRefreshTimes] = useAtom(lastDataRefreshAtom);
  const [activeTab] = useAtom(activeTabAtom);
  const [, forceRefreshTasks] = useAtom(forceRefreshTasksAtom);
  const [, forceRefreshProjects] = useAtom(forceRefreshProjectsAtom);
  const [isTranslationEnabled, setTranslationEnabled] = useAtom(translationEnabledAtom);
  const triggerUpdate = useSetAtom(triggerTranslationUpdateAtom);
  const { t, i18n } = useTranslation(['v0/common']);

  const loadCache = useSetAtom(loadTranslationCacheAtom);
  useEffect(() => {
    loadCache();
  }, [loadCache]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
      return;
    }

    if (activeTab === 'tasks') {
      forceRefreshTasks();
    } else if (activeTab === 'projects') {
      forceRefreshProjects();
    }
  };

  const handleTranslationToggle = () => {
    console.log('[TitleBar] Toggle translation clicked. Current state:', isTranslationEnabled);
    const newState = !isTranslationEnabled;
    setTranslationEnabled(newState);
    console.log('[TitleBar] New translation state set:', newState);
    if (newState) {
      console.log('[TitleBar] Triggering translation update...');
      triggerUpdate();
    }
  };

  const getRefreshTimeText = (tab: TabType) => {
    const timestamp = lastRefreshTimes[tab];
    if (!timestamp) return null;

    const now = Date.now();
    const diffSeconds = Math.floor((now - timestamp) / 1000);

    if (diffSeconds < 5) return t('v0/common:timeAgo.justNow', '刚刚');
    if (diffSeconds < 60) return t('common:timeAgo.seconds', { count: diffSeconds });
    if (diffSeconds < 3600) return t('common:timeAgo.minutes', { count: Math.floor(diffSeconds / 60) });
    if (diffSeconds < 86400) return t('common:timeAgo.hours', { count: Math.floor(diffSeconds / 3600) });
    return new Date(timestamp).toLocaleString();
  };

  const refreshTimeText = getRefreshTimeText(activeTab);

  return (
    <div className="sticky top-0 bg-surface-light dark:bg-surface-dark shadow-sm z-50">
      <div className="flex items-center justify-between px-4 h-12">
        <div className="flex items-center min-w-[80px]">
          {showBackButton && (
            <button
              onClick={handleBack}
              className="p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none"
            >
              <FiChevronLeft size={24} />
            </button>
          )}
          <button
            onClick={handleTranslationToggle}
            className={`p-1.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 transition-colors duration-150 ease-in-out
              ${
                isTranslationEnabled
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            title={t('v0/common:translation.toggle', '切换翻译')}
          >
            <span className="text-sm font-medium px-1">{t('v0/common:translation.icon')}</span>
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center text-center min-w-0">
          <h1 className="font-medium text-base truncate w-full">{title}</h1>
          {/* {refreshTimeText && !isRefreshing && (
            <p className="text-[10px] text-gray-400 dark:text-gray-500 -mt-0.5">
              {i18n.language === 'ru'
                ? t('common:refresh.updatedAgo', {
                    time: refreshTimeText === t('v0/common:timeAgo.justNow')
                      ? refreshTimeText.toLowerCase()
                      : refreshTimeText
                  })
                : `${refreshTimeText}${t('v0/common:updated', '')}`
              }
            </p>
          )} */}
        </div>

        <div className="flex items-center justify-end min-w-[80px]">
          {customAction}

          {showRefreshButton && (
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`p-2 focus:outline-none ${isRefreshing ? 'text-blue-500 animate-spin' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
            >
              <FiRefreshCw size={18} />
            </button>
          )}
        </div>
      </div>

      {isRefreshing && (
        <div className="h-0.5 bg-border-light dark:bg-border-dark overflow-hidden">
          <div className="h-full bg-blue-500 animate-progressBar"></div>
        </div>
      )}
    </div>
  );
};